import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

axios.defaults.withCredentials = false
axios.defaults.headers.common['Accept'] = 'application/json'

export interface MondayConfigResponse {
  is_active: boolean
  has_api_token: boolean
  last_sync_at: string | null
}

export interface MondayStatusResponse {
  is_configured: boolean
  mapped_boards_count: number
  last_sync_at: string | null
}

export interface MondaySaveConfigPayload {
  api_token?: string
}

export interface MondayImportResponse {
  queued: boolean
  message: string
}

export interface MondayBoardOption {
  id: string
  name: string
}

export interface MondayBoardMapping {
  id: number
  board_id: string
  board_name: string | null
  project_id: number | null
  project_name: string | null
}

class MondayApiService {
  private getAuthHeaders() {
    const authKey = 'impersonating-auth-token'
    const authData = localStorage.getItem(authKey)

    let token: string | null = null
    if (authData) {
      try {
        const auth = JSON.parse(authData)
        token = auth.api_token
      } catch (error) {
        console.error('Erro ao parsear dados de autenticação:', error)
      }
    }

    if (!token) {
      token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        localStorage.getItem('auth_token')
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  async getConfig(): Promise<MondayConfigResponse | null> {
    const response = await axios.get(`${API_BASE_URL}/api/monday/config`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.success) {
      return response.data.data?.config ?? null
    }
    throw new Error(response.data.message || 'Erro ao carregar configuração')
  }

  async saveConfig(payload: MondaySaveConfigPayload): Promise<void> {
    const response = await axios.post(`${API_BASE_URL}/api/monday/config`, payload, {
      headers: this.getAuthHeaders(),
    })
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao salvar configuração')
    }
  }

  async getStatus(): Promise<MondayStatusResponse> {
    const response = await axios.get(`${API_BASE_URL}/api/monday/status`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.success) {
      return response.data.data
    }
    throw new Error(response.data.message || 'Erro ao obter status')
  }

  async searchBoards(query = ''): Promise<MondayBoardOption[]> {
    const response = await axios.get(`${API_BASE_URL}/api/monday/boards`, {
      headers: this.getAuthHeaders(),
      params: query ? {q: query} : undefined,
    })
    if (response.data.status === 'success') {
      return response.data.data?.boards ?? []
    }
    throw new Error(response.data.message || 'Erro ao buscar boards')
  }

  async getBoardMappings(): Promise<MondayBoardMapping[]> {
    const response = await axios.get(`${API_BASE_URL}/api/monday/board-mappings`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status === 'success') {
      return response.data.data?.mappings ?? []
    }
    throw new Error(response.data.message || 'Erro ao carregar mapeamentos')
  }

  async createBoardMapping(data: {
    board_id: string
    board_name?: string | null
    project_id: number
  }): Promise<MondayBoardMapping> {
    const response = await axios.post(`${API_BASE_URL}/api/monday/board-mappings`, data, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status === 'success') {
      return response.data.data.mapping
    }
    throw new Error(response.data.message || 'Erro ao criar mapeamento')
  }

  async updateBoardMapping(
    id: number,
    data: {project_id: number; board_name?: string | null}
  ): Promise<MondayBoardMapping> {
    const response = await axios.put(`${API_BASE_URL}/api/monday/board-mappings/${id}`, data, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status === 'success') {
      return response.data.data.mapping
    }
    throw new Error(response.data.message || 'Erro ao atualizar mapeamento')
  }

  async deleteBoardMapping(id: number): Promise<void> {
    const response = await axios.delete(`${API_BASE_URL}/api/monday/board-mappings/${id}`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Erro ao remover mapeamento')
    }
  }

  async importItems(): Promise<MondayImportResponse> {
    const response = await axios.post(
      `${API_BASE_URL}/api/monday/items/import`,
      {},
      {headers: this.getAuthHeaders()}
    )
    if (response.data.status === 'success') {
      return {
        queued: Boolean(response.data.queued),
        message: response.data.message || 'Importação enfileirada.',
      }
    }
    throw new Error(response.data.message || 'Erro ao importar itens')
  }
}

export const mondayApiService = new MondayApiService()
